const sameIdentity = (left, right) => left?.uid === right?.uid
  && String(left?.organizationId) === String(right?.organizationId);

async function readSessionIdentity(signal) {
  // This existing cookie-authenticated endpoint does not mint new API tokens.
  const response = await fetch('/api/v1/users/multiple_organization_login_enabled', {
    credentials: 'same-origin',
    cache: 'no-store',
    redirect: 'error',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!response.ok) return null;
  const { user } = await response.json();
  if (!user?.email || user.current_organization_id == null) return null;
  return { uid: user.email, organizationId: user.current_organization_id };
}

// A rejected socket's monitor intentionally stops. Returning to that tab after
// signing in elsewhere may resume it, but only after Rails confirms its actor.
// Healthy connections and ordinary network recovery remain ActionCable's job.
export default function startCableSessionRecovery({
  cable,
  getIdentity,
  fetchIdentity = readSessionIdentity,
  onIdentityChange = () => window.location.reload(),
}) {
  let disposed = false;
  let pending = null;
  const stopped = () => cable.subscriptions.subscriptions.length > 0
    && !cable.connection.isActive() && !cable.connection.monitor.isRunning();

  const recover = async () => {
    const identity = getIdentity();
    if (disposed || pending || document.visibilityState === 'hidden'
      || !identity?.uid || identity.organizationId == null || !stopped()) return;

    const request = new AbortController();
    pending = request;
    try {
      const current = await fetchIdentity(request.signal);
      if (disposed || !current || !sameIdentity(identity, getIdentity()) || !stopped()) return;
      if (sameIdentity(identity, current)) {
        cable.connect();
      } else {
        // Never attach the old screen's subscriptions to a different user/org.
        onIdentityChange();
      }
    } catch {
      // A failed check stays stopped. Another user focus can check again;
      // there is no timer or automatic retry loop for rejected sessions.
    } finally {
      pending = null;
    }
  };

  window.addEventListener('focus', recover);
  document.addEventListener('visibilitychange', recover);
  return () => {
    disposed = true;
    pending?.abort();
    window.removeEventListener('focus', recover);
    document.removeEventListener('visibilitychange', recover);
  };
}
